import { useState } from 'react';
import { once } from 'ramda';

import { isServer } from '../lib/utils';
import { trackGroup } from '../lib/analytics';

const getGroupId = ({ id, variant }) => {
  return `experiment-${id}-panel-${variant}`;
};

/**
 * Equivalent of the {@link https://github.com/wbetterdev/waybetter-mobile/blob/develop/src/navigators/AppNavigator/helpers/bootstrap-remote-config/send-experiment-events.js#L11|WB app function}.
 *
 * @param {object} opts
 * @param {string} opts.id
 * @param {string} opts.variant
 * @param {string} opts.analyticsProperty
 */
const sendGroupEvent = ({ id, variant, analyticsProperty }) => {
  if (!analytics?.group) {
    console.warn('analytics?.group is not found');
    return;
  }

  trackGroup(getGroupId({ id, variant }), {
    analyticsProperty,
    experiment: id,
    panel: variant,
  });
};

/**
 * Send activation event to Google Optimize once the component is ready.
Report which variant the user was put in, for the experiment.

Loosely based on {@link https://github.com/getanwar/google-optimize-react-hooks|the google-optimize-react-hooks lib.}

The "trackExperimentViewed" callback is a global function used to track
in GA when a user viewed an experiment (see main.html).
 *
 * @returns {object} Returns two function for activating the experiment and
 * also getting the current tests from state.
 */
export function useABTest() {
  const [tests, setTests] = useState({});
  const callbacks = {};

  const activate = ({ altExperimentId, experimentId: name }) => {
    if (isServer || !name) return;

    if (!dataLayer) {
      console.warn(
        'Google Tag Manager dataLayer was not found. Ensure script is configured correctly in html template.'
      );
      return;
    }

    dataLayer.push({ event: 'optimize.activate' });

    const callback = once((variant, experimentId, analyticsProperty) => {
      if (name !== experimentId) return;

      setTests({ ...tests, [experimentId]: { variant } });
      const id = altExperimentId || experimentId;

      sendGroupEvent({ id, variant, analyticsProperty });
    });
    callbacks[name] = callback;

    gtag('event', 'optimize.callback', { name, callback: callbacks[name] });
  };

  const getTest = (id) => (tests[id] ? { ...tests[id] } : null);

  return { activate, getTest };
}
